import type { AppFeedbackConfiguration } from 'apiConnector/appFeedback';
import { appFeedbackApi } from 'apiConnector/appFeedback';
import { useCallback, useEffect, useState } from 'react';
import { httpClient } from 'utils/http/httpClient';
import type { HttpError } from 'utils/http/httpClient.model';

export type FetchStatus = 'loading' | 'error' | 'unauthorized' | 'success';

function isForbidden(httpErrorCode: number) {
  return httpErrorCode === 401 || httpErrorCode === 403;
}

const api = appFeedbackApi(httpClient);

export function useAppFeedbackConfiguration(): [
  status: FetchStatus,
  data?: Readonly<AppFeedbackConfiguration>,
  onRetry?: () => void,
] {
  const [data, setData] = useState<Readonly<AppFeedbackConfiguration>>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [httpErrorCode, setHttpErrorCode] = useState<number | null>();
  const [fetchDataRetries, setFetchDataRetries] = useState<number>(0);

  const handleRetry = useCallback(() => {
    setFetchDataRetries(fetchDataRetries + 1);
  }, [fetchDataRetries]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to rerun this effect every time fetchDataRetries is updated
  useEffect(() => {
    setIsLoading(true);
    const subscription = api.getConfiguration().subscribe({
      next(response) {
        setIsLoading(false);
        setHttpErrorCode(null);
        setData(response.data);
      },
      error({ status }: HttpError) {
        setHttpErrorCode(status);
        setIsLoading(false);
      },
    });
    return function cleanup() {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    };
  }, [fetchDataRetries]);

  if (isLoading) {
    return ['loading'];
  }
  if (httpErrorCode) {
    return isForbidden(httpErrorCode) ? ['unauthorized'] : ['error', undefined, handleRetry];
  }

  return ['success', data];
}
